/***
*
*   AUTHENTICATION
*   Auth provider to manage auth functions throughout
*   the application. <PrivateRoute> component to
*   protect internal application routes from unauthenticated
*   access.
*
**********/

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Redirect, Route } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useAPI, Event } from 'components/lib';
import permissions from './permissions';
import { cacheUserData, getCachedUserData, cacheOrgData, clearCache, migrateFromLocalStorage } from '../utils/sessionCache';
import { sanitizeRedirect } from '../utils/sanitize';
import { debugLog } from '../utils/debugLog';

// auth context
export const AuthContext = React.createContext();

export function AuthProvider(props){

  // Initialize user state from cache (after migration if needed)
  const [user, setUser] = useState(() => {
    // Run migration synchronously before reading cache
    const migrated = migrateFromLocalStorage();
    const cachedUser = getCachedUserData();
    debugLog('[AuthProvider] - Initializing:', { migrated, hasCachedUser: !!cachedUser, userId: cachedUser?.id });
    return cachedUser;
  });
  const [orgs, setOrgs] = useState([]);
  const auth = useAPI(user ? '/api/auth' : null);

  useEffect(() => {

    // update the auth status
    if (!auth.loading && auth.data){
      debugLog('[auth] - Auth check result:', { authenticated: auth.data.authenticated, hasUser: !!user });

      auth.data.authenticated ? update(auth.data) : signout();

    }
  }, [auth]);

  // function wsMessageHandler(handler) {
  //   setWsMsgHandler(handler);
  // }
  
  // function sendWsMessage(msg) {
  //   ws?.send(msg);
  // }

  function signin(res, retUrl=''){
    if (res.data) {
      debugLog('[signin] - Caching user data and setting state');
      // Store user data in sessionStorage
      cacheUserData(res.data);
      // Update React state
      setUser(res.data);
      debugLog('[signin] - User state set:', { id: res.data.id, email: res.data.email, onboarded: res.data.onboarded });
      Event('signin');
      let redirect = res.data.onboarded ? '/home' : '/welcome';
      if(retUrl !== '') {
        redirect = sanitizeRedirect(retUrl, '/home');
      }
      if (res.data.show_qbo_connect === true) {
        redirect = '/authUri';
      }
      debugLog('[signin] - Redirecting to:', redirect);
      return window.location = redirect;
    }
  }

  async function signout(){
    axios({ method: 'delete', url: '/api/auth' });

    // Preserve sticky menu preference before clearing storage
    const stickyMenuPreference = localStorage.getItem('ledgerview-menu-sticky');

    // Clear all session and local storage
    clearCache();
    localStorage.clear();

    // Restore sticky menu preference (UI setting only, not sensitive)
    if (stickyMenuPreference !== null) {
      localStorage.setItem('ledgerview-menu-sticky', stickyMenuPreference);
    }

    Cookies.remove('jwt');
    Cookies.remove('accountId');
    window.location = '/signin'
  }

  function hasAccountSetting(val) {
    let bRet = false;

    // over ride for edit_rules setting
    if(val === "edit_rules") {
      return true;
    }

    if(user.account_settings) {
      if(user.account_settings[val]) {
        bRet = user.account_settings[val]
      }
    }

    return bRet;
  }

  async function switchAccount(id, retUrl=''){

    debugLog('[switchAccount]', id, retUrl);
    const res = await axios({

      method: 'post',
      url: '/api/auth/switch',
      data: { account: id }

    });

    if (res.data)
      signin(res, retUrl);

  }

  async function switchApp(id, retUrl='', account = null){
    const res = await axios({ 
      
      method: 'post', 
      url: '/api/auth/switchApp',
      data: { appConnection: id, account: account }
    
    });

    if (res.data)
      signin(res, retUrl)
  }

  function update(data){

    // Get current cached user or use current state as fallback
    const cachedUser = getCachedUserData() || user;

    if (!cachedUser) {
      // If no cached user and no current user, just cache and set the new data
      cacheUserData(data);
      setUser(data);
      return;
    }

    let updatedUser = { ...cachedUser };

    for (let key in data) {
      if (Array.isArray(data[key])) {
        updatedUser[key] = data[key]
      }
      else if (typeof data[key] === 'object' && data[key] !== null) {
        for (let innerKey in data[key]){
          if (!updatedUser[key]) updatedUser[key] = {};
          updatedUser[key][innerKey] = data[key][innerKey]
        }
      }
      else {
        updatedUser[key] = data[key];
      }
    }

    cacheUserData(updatedUser);
    setUser(updatedUser);
  }

  function updateOrgs(data) {
    if (data?.length) {
      setOrgs(data);
      cacheOrgData(data);
    }
  }

  return (
    <AuthContext.Provider value={{

      user: user,
      signin: signin,
      signout: signout,
      update: update,
      switchAccount: switchAccount,
      switchApp: switchApp,
      hasAccountSetting: hasAccountSetting,
      orgs: orgs,
      permission: permissions[user?.permission],
      is_firm: user?.is_firm, // Add is_firm value to the context
      updateOrgs: updateOrgs,

    }}

    {...props} />
  );
}

// custom route object checks for an auth token before
// rendering the route – redirects if token is not present
export function PrivateRoute({permission, accountType, ...props}){

  return <Route {...props} render={ data => {

    // check user
    const user = getCachedUserData();
    
    if (user){
    //   if (permissions[user.permission][props.permission]){

    //     if(props.path.includes('/ai')) {
        
    //           if((!user.account_settings) || (user.account_settings?.dev === false)) {
    //           return <Redirect to='/signin' />;
    //         }
    //     }

    //     // if( props.path.includes('/firm')) {
        
    //     //   if((!user.account_settings) || (user.account_settings?.firm === false)) {
    //     //       return <Redirect to='/signin' />;
    //     //     }
    //     // }

    //     // user has no plan
    //     // if (!user.plan && path !== '/account' && path !== '/signup/plan')
    //     //   return <Redirect to='/signup/plan'/>;
  
    //     // user has no subscription
    //     // if ((user.subscription !== 'active' && user.subscription !== 'trialing') && user.permission !== 'master'){

    //     //   if (!path.includes('/account/billing') &&
    //     //       !path.includes('/signup/plan') &&
    //     //       !path.includes('/account/upgrade') && 
    //     //       !path.includes('/account')){

    //     //       return <Redirect to='/account/billing' />

    //     //   }
    //     // }

        // Check account type restrictions for health score only accounts
        if (user.account_type === 'health_score_only') {
          // Health score only accounts can only access dashboard and health check
          const allowedPaths = ['/home', '/dashboard', '/healthScore', '/help', '/app/error', '/client/connections', '/app/account', '/profile/index'];
          // Allow access to account pages with IDs (e.g., /account/123)
          const isAccountPage = props.path && props.path.startsWith('/account/');
          const isAllowed = allowedPaths.some(path => props.path === path) || isAccountPage;

          if (!isAllowed) {
            return <Redirect to='/dashboard' />;
          }
        }

        // Check if route requires full_monitoring account type
        if (accountType === 'full_monitoring' && user.account_type !== 'full_monitoring') {
          return <Redirect to='/dashboard' />;
        }

        // Check if the user has the required permission
        // console.log('user.permission', permissions[user.permission], permission); 
        if (permissions[user.permission]?.[permission]) {
          return <props.render {...props} />;
        }
        // user is good
        return <Redirect to='/signin' />;

      // }
    }

    // user is not authenticated
    return <Redirect to='/signin' />;

  }}/>
}
